/** Редактор справочника артикулов — порт таблицы tech-вкладки (build-скрипт, стр. 8089-8202):
 * тип продукта/характеристика — селекты из каталогов, объём — число, теги — с нормализацией
 * formatTags по Enter/запятой, сохранение по change. CSV: шаблон/экспорт/импорт
 * (шапка экспорта — фикс бага шаблона оригинала). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import type { OrdersMatrixReport } from "@/api/orders";
import * as api from "@/api/orders-planning";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { extractErrorMessage } from "@/lib/api-errors";
import { buildAttrsCsv, downloadTextFile, formatTags, parseAttrsCsv } from "../csv";
import { matchesSearch } from "../metrics";

const KEYS = {
  attrs: ["orders", "refs", "attrs"],
  types: ["orders", "refs", "product-types"],
  chars: ["orders", "refs", "characteristics"],
};

interface DirectoryRow {
  article: string;
  name: string;
  brand: string;
  code_1c: string;
  id_1c: string;
}

export function AttrsEditor({
  report,
  canEdit,
}: {
  report: OrdersMatrixReport | undefined;
  canEdit: boolean;
}): React.ReactElement {
  const qc = useQueryClient();
  const attrs = useQuery({ queryKey: KEYS.attrs, queryFn: api.listAttrs });
  const types = useQuery({ queryKey: KEYS.types, queryFn: api.listProductTypes });
  const chars = useQuery({ queryKey: KEYS.chars, queryFn: api.listCharacteristics });

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 180);
    return () => clearTimeout(t);
  }, [searchInput]);

  const attrsByArticle = useMemo(
    () => new Map((attrs.data ?? []).map((a) => [a.article, a])),
    [attrs.data],
  );

  // Все артикулы: из отчёта (уникальные) + артикулы, у которых есть только атрибуты
  const rows = useMemo<DirectoryRow[]>(() => {
    const map = new Map<string, DirectoryRow>();
    for (const r of report?.rows ?? []) {
      if (!map.has(r.article)) {
        map.set(r.article, {
          article: r.article,
          name: r.name ?? r.article,
          brand: r.brand ?? "",
          code_1c: r.code_1c ?? "",
          id_1c: r.id_1c ?? "",
        });
      }
    }
    for (const a of attrs.data ?? []) {
      if (!map.has(a.article)) {
        map.set(a.article, { article: a.article, name: a.article, brand: "", code_1c: "", id_1c: "" });
      }
    }
    return [...map.values()]
      .filter((r) => matchesSearch(r, search))
      .sort((a, b) => a.article.localeCompare(b.article, "ru"));
  }, [report, attrs.data, search]);

  const save = useMutation({
    mutationFn: api.upsertAttrs,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders", "refs"] }),
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить атрибут")),
  });

  const bulk = useMutation({
    mutationFn: api.bulkAttrs,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["orders", "refs"] });
      toast.success(`Импортировано строк: ${r.imported}`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Импорт не удался")),
  });

  const fileRef = useRef<HTMLInputElement>(null);

  const csvSource = () =>
    rows.map((r) => {
      const a = attrsByArticle.get(r.article);
      return {
        article: r.article,
        name: r.name,
        product_type: a?.product_type ?? "",
        characteristic: a?.characteristic ?? "",
        volume_ml: a?.volume_ml ?? null,
        merge_product: a?.merge_product ?? "",
        semi_finished: a?.semi_finished ?? "",
      };
    });

  if (attrs.isLoading || types.isLoading || chars.isLoading) {
    return <div className="text-sm text-muted-foreground">Загружаю справочник…</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Поиск по справочнику"
          className="h-9 w-72"
        />
        <span className="text-xs text-muted-foreground">строк: {rows.length}</span>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadTextFile("article-groups-template.csv", buildAttrsCsv(csvSource(), false))}
          >
            Шаблон CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadTextFile("article-groups-saved.csv", buildAttrsCsv(csvSource(), true))}
          >
            Экспорт CSV
          </Button>
          {canEdit && (
            <>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                Импорт CSV
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  const parsed = parseAttrsCsv(await file.text());
                  if (!parsed.length) {
                    toast.error("В файле нет строк справочника");
                    return;
                  }
                  bulk.mutate(parsed);
                }}
              />
            </>
          )}
        </div>
      </div>

      <div className="max-h-[calc(100vh-320px)] overflow-auto rounded-md border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-background">
            <tr className="border-b text-muted-foreground">
              <th className="px-2 py-1.5 text-left font-medium">Артикул / название</th>
              <th className="px-2 py-1.5 text-left font-medium">Тип продукта</th>
              <th className="px-2 py-1.5 text-left font-medium">Характеристика</th>
              <th className="px-2 py-1.5 text-right font-medium">Объём, мл</th>
              <th className="px-2 py-1.5 text-left font-medium">Продукт объединения</th>
              <th className="px-2 py-1.5 text-left font-medium">ПФ</th>
              <th className="px-2 py-1.5 text-left font-medium">Комментарий</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <AttrsRow
                key={r.article}
                row={r}
                attrs={attrsByArticle.get(r.article)}
                types={types.data ?? []}
                chars={chars.data ?? []}
                canEdit={canEdit}
                onSave={(patch) => save.mutate({ article: r.article, ...patch })}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AttrsRow({
  row,
  attrs,
  types,
  chars,
  canEdit,
  onSave,
}: {
  row: DirectoryRow;
  attrs: api.ArticleAttrs | undefined;
  types: api.ProductType[];
  chars: api.Characteristic[];
  canEdit: boolean;
  onSave: (patch: Partial<api.ArticleAttrsUpsert>) => void;
}): React.ReactElement {
  const [volume, setVolume] = useState(String(attrs?.volume_ml ?? ""));
  const [mergeProduct, setMergeProduct] = useState(attrs?.merge_product ?? "");
  const [semi, setSemi] = useState(attrs?.semi_finished ?? "");
  const [comment, setComment] = useState(attrs?.comment ?? "");
  useEffect(() => {
    setVolume(String(attrs?.volume_ml ?? ""));
    setMergeProduct(attrs?.merge_product ?? "");
    setSemi(attrs?.semi_finished ?? "");
    setComment(attrs?.comment ?? "");
  }, [attrs]);

  const sel = "h-7 w-full rounded border bg-background px-1 text-xs";
  const inp =
    "h-7 w-full rounded border bg-background px-1.5 text-xs focus:border-primary focus:outline-none";

  return (
    <tr className="border-b last:border-0">
      <td className="max-w-56 px-2 py-1">
        <div className="truncate font-medium">{row.article}</div>
        <div className="truncate text-[10px] text-muted-foreground" title={row.name}>
          {row.name}
        </div>
      </td>
      <td className="w-36 px-2 py-1">
        <select
          className={sel}
          disabled={!canEdit}
          value={attrs?.product_type ?? ""}
          onChange={(e) => onSave({ product_type: e.target.value })}
        >
          <option value="">Не задано</option>
          {attrs?.product_type && !types.some((t) => t.name === attrs.product_type) && (
            <option value={attrs.product_type}>{attrs.product_type}</option>
          )}
          {types.map((t) => (
            <option key={t.id} value={t.name}>{t.name}</option>
          ))}
        </select>
      </td>
      <td className="w-36 px-2 py-1">
        <select
          className={sel}
          disabled={!canEdit}
          value={attrs?.characteristic ?? ""}
          onChange={(e) => onSave({ characteristic: e.target.value })}
        >
          <option value="">Без характеристики</option>
          {attrs?.characteristic && !chars.some((c) => c.name === attrs.characteristic) && (
            <option value={attrs.characteristic}>{attrs.characteristic}</option>
          )}
          {chars.map((c) => (
            <option key={c.id} value={c.name}>{c.name}</option>
          ))}
        </select>
      </td>
      <td className="w-20 px-2 py-1">
        <input
          type="number"
          min={0}
          step={1}
          placeholder="500"
          className={`${inp} text-right`}
          disabled={!canEdit}
          value={volume}
          onChange={(e) => setVolume(e.target.value)}
          onBlur={() => onSave({ volume_ml: volume ? Math.max(0, Math.round(Number(volume))) : null })}
        />
      </td>
      <td className="w-44 px-2 py-1">
        <input
          className={inp}
          disabled={!canEdit}
          placeholder="#Гель"
          value={mergeProduct}
          onChange={(e) => setMergeProduct(e.target.value)}
          onBlur={() => {
            const v = formatTags(mergeProduct);
            setMergeProduct(v);
            onSave({ merge_product: v });
          }}
        />
      </td>
      <td className="w-44 px-2 py-1">
        <input
          className={inp}
          disabled={!canEdit}
          placeholder="#Табак-ваниль"
          value={semi}
          onChange={(e) => setSemi(e.target.value)}
          onBlur={() => {
            const v = formatTags(semi);
            setSemi(v);
            onSave({ semi_finished: v });
          }}
        />
      </td>
      <td className="px-2 py-1">
        <input
          className={inp}
          disabled={!canEdit}
          placeholder="Комментарий"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => onSave({ comment: comment.trim() })}
        />
      </td>
    </tr>
  );
}
